import { useCallback, useEffect, useMemo } from "react"

import { useAttachments } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { isEmptyContentTag } from "../helpers"
import { useThreadContext } from "../thread-context"
import { useComposerState } from "./useComposerState"

type AttachmentState = "loading" | "uploaded" | "error"
export interface Attachment {
	id: string
	file: File
	// set async, so marked as optional
	url?: string | null

	name: string
	type: string
	size: number
	state: AttachmentState
}

export function useEditorState({
	initialContent,
}: {
	initialContent?: string
}) {
	const { thread } = useThreadContext()
	const { entries, setEntry } = useComposerState()
	const entry = useMemo(() => entries[thread.id], [entries, thread.id])
	const getSignedUrlMutation = api.file.getUrl.useMutation()
	const deleteFileUrlMutation = api.file.deleteFileUrl.useMutation()

	const content = useMemo(
		() =>
			isEmptyContentTag(entry?.content)
				? (initialContent ?? "")
				: entry?.content,
		[entry?.content, initialContent],
	)
	const commentContent = useMemo(() => entry?.commentContent ?? "", [entry])

	const editorId = useMemo(() => {
		return thread.id //+ "-editor"
	}, [thread.id])

	const { attachments, formattedAttachments, ...attachmentsHandlers } =
		useAttachments({
			contextId: editorId,
			getSignedUrl: getSignedUrlMutation.mutateAsync,
			deleteFileUrl: async ({ fileUrl }) => {
				await deleteFileUrlMutation.mutateAsync({ fileUrl })
			},
			errorHandler: ({ title, description }) => {
				toast.error({
					title,
					description,
				})
			},
		})

	const {
		attachments: commentAttachments,
		formattedAttachments: commentFormattedAttachments,
		...commentAttachmentsHandlers
	} = useAttachments({
		contextId: `${editorId}-comments`,
		getSignedUrl: getSignedUrlMutation.mutateAsync,
		deleteFileUrl: async ({ fileUrl }) => {
			await deleteFileUrlMutation.mutateAsync({ fileUrl })
		},
		errorHandler: ({ title, description }) => {
			toast.error({
				title,
				description,
			})
		},
	})

	const updateContent = useCallback(
		(newContent: string) => {
			setEntry(thread.id, {
				content: newContent,
			})
		},
		[thread.id, setEntry],
	)

	const initEntry = useCallback(() => {
		setEntry(thread.id, {})
	}, [setEntry, thread.id])

	useEffect(() => {
		// make sure to init the entry, otherwise there will be side effect for the first update call
		initEntry()
	}, [initEntry, thread.id])

	const updateCommentContent = useCallback(
		(newContent: string) => {
			setEntry(thread.id, {
				commentContent: newContent,
			})
		},
		[thread.id, setEntry],
	)

	const reset = useCallback(() => {
		// Also wipe the AI-draft origin tags so the next inbound AI draft on
		// this same thread can auto-apply cleanly. Without this, source stays
		// "ai" / "user" across sends and the auto-seed gate keeps blocking.
		setEntry(thread.id, {
			content: "",
			source: null,
			appliedAiDraftAt: null,
		})
		attachmentsHandlers.clearAttachments()
	}, [setEntry, thread.id, attachmentsHandlers])

	const resetComment = useCallback(() => {
		setEntry(thread.id, {
			commentContent: "",
		})
		commentAttachmentsHandlers.clearAttachments()
	}, [setEntry, thread.id, commentAttachmentsHandlers])

	return {
		content,
		commentContent,
		updateContent,
		updateCommentContent,
		attachments,
		formattedAttachments,
		reset,
		resetComment,
		attachmentsHandlers,
		commentAttachments,
		commentAttachmentsHandlers,
		commentFormattedAttachments,
	}
}
